import type { DisplayRates } from './api/types';
import { currentLocale } from './i18n';
import { mulDecimal } from './utils/decimal';
import { formatFiat } from './utils/format';

/**
 * Display currency: a second, approximate fiat figure next to the amount the merchant asked
 * for. It never replaces the amount due or the crypto amount. Rates come from the session's
 * `display_rates` (1 unit of the session currency in other fiats); without them the selector
 * is hidden and nothing is converted.
 */

const STORAGE_KEY = 'cv-display-currency';

/** Locale -> the currency a payer there most likely thinks in. Region first, then language. */
const BY_REGION: Record<string, string> = {
  US: 'USD', GB: 'GBP', IE: 'EUR', CA: 'CAD', AU: 'AUD', NZ: 'NZD', IN: 'INR', SG: 'SGD', HK: 'HKD',
  ZA: 'ZAR', NG: 'NGN', PH: 'PHP',
  MX: 'MXN', AR: 'ARS', CO: 'COP', CL: 'CLP', PE: 'PEN', UY: 'UYU', ES: 'EUR',
  BR: 'BRL', PT: 'EUR',
  FR: 'EUR', DE: 'EUR', AT: 'EUR', IT: 'EUR', NL: 'EUR', BE: 'EUR', FI: 'EUR', GR: 'EUR', LU: 'EUR',
  CH: 'CHF', LI: 'CHF',
  JP: 'JPY', CN: 'CNY', TW: 'TWD', KR: 'KRW', TR: 'TRY', RU: 'RUB', UA: 'UAH',
  SA: 'SAR', AE: 'AED', EG: 'EGP', QA: 'QAR', KW: 'KWD', MA: 'MAD', JO: 'JOD', BH: 'BHD', OM: 'OMR',
  PL: 'PLN', SE: 'SEK', NO: 'NOK', DK: 'DKK', CZ: 'CZK', HU: 'HUF', RO: 'RON', IL: 'ILS',
  ID: 'IDR', TH: 'THB', VN: 'VND', MY: 'MYR',
};
const BY_LANGUAGE: Record<string, string> = {
  ja: 'JPY', ko: 'KRW', zh: 'CNY', tr: 'TRY', ru: 'RUB', de: 'EUR', fr: 'EUR', it: 'EUR', nl: 'EUR',
  pt: 'BRL', pl: 'PLN', sv: 'SEK', nb: 'NOK', da: 'DKK', cs: 'CZK', hu: 'HUF', uk: 'UAH', he: 'ILS',
  id: 'IDR', th: 'THB', vi: 'VND', hi: 'INR',
};

export function currencyForLocale(tag: string): string | null {
  const parts = tag.replace(/_/g, '-').split('-');
  const region = parts.find((p, i) => i > 0 && /^[A-Za-z]{2}$/.test(p))?.toUpperCase();
  if (region && BY_REGION[region]) return BY_REGION[region];
  return BY_LANGUAGE[parts[0].toLowerCase()] ?? null;
}

const ISO = /^[A-Za-z]{3}$/;

export function storedDisplayCurrency(): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && ISO.test(v) ? v.toUpperCase() : null;
  } catch {
    return null;
  }
}

export function rememberDisplayCurrency(code: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, code.toUpperCase());
  } catch {
    /* storage unavailable */
  }
}

/** ISO codes the payer can pick: the session currency plus every rate the API sent. */
export function availableCurrencies(sessionCurrency: string, rates: DisplayRates | null | undefined): string[] {
  const set = new Set<string>([sessionCurrency.toUpperCase()]);
  for (const k of Object.keys(rates?.rates ?? {})) if (ISO.test(k)) set.add(k.toUpperCase());
  return [...set].sort((a, b) => (a === sessionCurrency.toUpperCase() ? -1 : b === sessionCurrency.toUpperCase() ? 1 : a.localeCompare(b)));
}

/**
 * Initial display currency: `?currency=`, then the stored choice, then the browser/UI locale,
 * then the merchant's `suggested_currency` hint, then the session currency. Anything not in
 * `available` falls through to the next source.
 */
export function detectDisplayCurrency(
  sessionCurrency: string,
  available: string[],
  explicit?: string | null,
  merchantHint?: string | null,
): string {
  const ok = (c: string | null | undefined) => (c && available.includes(c.toUpperCase()) ? c.toUpperCase() : null);
  const url = new URLSearchParams(location.search).get('currency');
  const fromUrl = ok(explicit ?? url);
  if (fromUrl) return fromUrl;
  const stored = ok(storedDisplayCurrency());
  if (stored) return stored;
  for (const tag of [...(navigator.languages ?? []), navigator.language, currentLocale()]) {
    const c = ok(tag ? currencyForLocale(tag) : null);
    if (c) return c;
  }
  return ok(merchantHint) ?? sessionCurrency.toUpperCase();
}

export function hasExplicitDisplayCurrency(): boolean {
  const url = new URLSearchParams(location.search).get('currency');
  return !!(url && ISO.test(url)) || !!storedDisplayCurrency();
}

/** Fraction digits Intl uses for a currency (JPY 0, KWD 3, most 2). */
export function fractionDigits(currency: string): number {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

/**
 * Convert `amount` (in `rates.base`) into `target` as a rounded decimal string, or null when
 * no rate exists. Exact BigInt arithmetic; the only rounding is to the target's minor unit.
 */
export function convertDisplay(amount: string, rates: DisplayRates | null | undefined, target: string): string | null {
  if (!rates || !rates.rates) return null;
  const code = target.toUpperCase();
  const rate = rates.rates[code] ?? rates.rates[code.toLowerCase()];
  if (typeof rate !== 'string' || !/^\d+(\.\d+)?$/.test(rate.trim())) return null;
  if (!/^-?\d+(\.\d+)?$/.test(amount.trim())) return null;
  try {
    return mulDecimal(amount, rate, fractionDigits(code));
  } catch {
    return null;
  }
}

/** Formatted approximate figure in `target`, or null when it cannot be computed. */
export function formatDisplay(amount: string, rates: DisplayRates | null | undefined, target: string, locale = currentLocale()): string | null {
  const v = convertDisplay(amount, rates, target);
  return v === null ? null : formatFiat(v, target.toUpperCase(), locale);
}
